import type { UsuarioDeSesion } from '@medchoice/shared';
import { Enlace, irA, useRuta } from '../../router';
import { useSesion } from '../../session/Sesion';
import { Avatar } from '../Avatar';
import { Icono } from '../Icono';
import { Logo } from './Logo';

interface EnlaceNav {
  texto: string;
  ruta: string;
  activoEn: readonly string[];
}

// Para el paciente, Inicio es su pantalla; para quien no entró, la portada.
const INICIO_PACIENTE: EnlaceNav = { texto: 'Inicio', ruta: '/inicio', activoEn: ['/inicio'] };
const INICIO_VISITANTE: EnlaceNav = { texto: 'Inicio', ruta: '/', activoEn: ['/', '/inicio'] };
const BUSCAR: EnlaceNav = { texto: 'Buscar', ruta: '/buscar', activoEn: ['/buscar', '/resultados', '/profesional/'] };

// Con sesión de paciente se muestra "Mis turnos"; sin sesión, los planes, que es lo
// que busca un profesional que llega por primera vez.
const NAV_PACIENTE = [INICIO_PACIENTE, BUSCAR, { texto: 'Mis turnos', ruta: '/mis-turnos', activoEn: ['/mis-turnos', '/turno/'] }];
const NAV_VISITANTE = [INICIO_VISITANTE, BUSCAR, { texto: 'Planes', ruta: '/planes', activoEn: ['/planes'] }];

const PANEL_POR_ROL = { profesional: '/pro/agenda', administrador: '/admin' } as const;

function estaActivo(enlace: EnlaceNav, camino: string): boolean {
  return enlace.activoEn.some((r) => camino === r || (r.length > 1 && r.endsWith('/') && camino.startsWith(r)));
}

export function EncabezadoPublico() {
  const { usuario } = useSesion();
  const { camino } = useRuta();
  const esPaciente = usuario?.rol === 'paciente';
  const enlaces = esPaciente ? NAV_PACIENTE : NAV_VISITANTE;

  return (
    <header className="encabezado">
      <div className="encabezado__interior">
        <Logo a="/" />
        <nav className="encabezado__nav" aria-label="Principal">
          {enlaces.map((e) => {
            const activo = estaActivo(e, camino);
            return (
              <Enlace
                key={e.ruta}
                a={e.ruta}
                className={activo ? 'pildora pildora--activa' : 'pildora'}
                aria-current={activo ? 'page' : undefined}
              >
                {e.texto}
              </Enlace>
            );
          })}
        </nav>
        <AccesoDeCuenta usuario={usuario} />
      </div>
    </header>
  );
}

function AccesoDeCuenta({ usuario }: { usuario: UsuarioDeSesion | null }) {
  const { salir } = useSesion();

  if (!usuario) {
    return <Enlace a="/login" className="encabezado__ingresar">Ingresar</Enlace>;
  }

  if (usuario.rol !== 'paciente') {
    return <Enlace a={PANEL_POR_ROL[usuario.rol]} className="encabezado__ingresar">Ir a mi panel</Enlace>;
  }

  const nombre = `${usuario.nombre} ${usuario.apellido}`;
  return (
    <div className="encabezado__cuenta">
      <Enlace a="/mi-cuenta" className="encabezado__perfil" title="Mi cuenta">
        <span className="encabezado__nombre">{nombre}</span>
        <Avatar nombre={nombre} tamano={34} decorativo />
        <span className="solo-lectores">Mi cuenta</span>
      </Enlace>
      <button
        type="button"
        className="boton-sutil encabezado__salir"
        title="Cerrar sesión"
        aria-label="Cerrar sesión"
        onClick={() => { void salir().finally(() => { irA('/login'); }); }}
      >
        <Icono nombre="log-out" tamano={17} />
      </button>
    </div>
  );
}
